import BigNumber from "bignumber.js";
import { Percent, Router } from "@koindx/v2-sdk";

// every hop keeps 99.75% of the input as liquidity provider fee
const FEE_FACTOR = new BigNumber(9975).div(10000);
const NO_SLIPPAGE = new Percent(new BigNumber(0));

const hasLiquidity = (pair) =>
  Boolean(pair && pair.reserve_0 && pair.reserve_1 && !pair.reserve_0.isZero() && !pair.reserve_1.isZero());
const involves = (pair, token) => pair.token_0.equals(token) || pair.token_1.equals(token);
const otherToken = (pair, token) => (pair.token_0.equals(token) ? pair.token_1 : pair.token_0);

// the SDK router throws (tiny-invariant) when a hop can not be computed
const tryTrade = (trade) => {
  try {
    return trade();
  } catch (error) {
    return null;
  }
};

/**
 * Lists every route (an ordered list of pairs) from currencyIn to currencyOut
 * using at most maxHops pairs, without repeating a pair or a token
 */
export const findRoutes = (pairs, currencyIn, currencyOut, maxHops = 3) => {
  const usable = (pairs || []).filter(hasLiquidity);
  const routes = [];
  const walk = (token, route, visited) => {
    if (route.length >= maxHops) return;
    for (const pair of usable) {
      if (route.includes(pair) || !involves(pair, token)) continue;
      const next = otherToken(pair, token);
      if (next.equals(currencyOut)) {
        routes.push([...route, pair]);
        continue;
      }
      if (visited.some((visitedToken) => visitedToken.equals(next))) continue;
      walk(next, [...route, pair], [...visited, next]);
    }
  };
  if (!currencyIn.equals(currencyOut)) {
    walk(currencyIn, [], [currencyIn]);
  }
  return routes;
};

/**
 * Returns the route with the highest output for an exact amount in, or null
 * { amount_in, amount_out_min, path, pairs }
 */
export const findBestTradeExactIn = (
  pairs,
  currencyIn,
  currencyOut,
  amountIn,
  slippage = NO_SLIPPAGE,
  { maxHops = 3 } = {}
) => {
  const _amountIn = new BigNumber(amountIn);
  if (!_amountIn.isFinite() || _amountIn.isLessThanOrEqualTo(0)) return null;
  let best = null;
  for (const route of findRoutes(pairs, currencyIn, currencyOut, maxHops)) {
    const trade = tryTrade(() => new Router(route, currencyIn, currencyOut).tradeExactIn(_amountIn, slippage));
    if (!trade) continue;
    const amountOutMin = trade.amount_out_min.integerValue(BigNumber.ROUND_DOWN);
    if (!amountOutMin.isFinite() || amountOutMin.isLessThan(1)) continue;
    if (!best || amountOutMin.isGreaterThan(best.amount_out_min)) {
      best = { amount_in: _amountIn, amount_out_min: amountOutMin, path: trade.path, pairs: route };
    }
  }
  return best;
};

/**
 * Returns the route with the lowest input for an exact amount out, or null
 * { amount_in_max, amount_out, path, pairs }
 */
export const findBestTradeExactOut = (
  pairs,
  currencyIn,
  currencyOut,
  amountOut,
  slippage = NO_SLIPPAGE,
  { maxHops = 3 } = {}
) => {
  const _amountOut = new BigNumber(amountOut);
  if (!_amountOut.isFinite() || _amountOut.isLessThanOrEqualTo(0)) return null;
  let best = null;
  for (const route of findRoutes(pairs, currencyIn, currencyOut, maxHops)) {
    const router = new Router(route, currencyIn, currencyOut);
    const trade = tryTrade(() => router.tradeExactOut(_amountOut, slippage));
    if (!trade) continue;
    // the required input is rounded up, rounding down could make it insufficient
    const amountInMax = trade.amount_in_max.integerValue(BigNumber.ROUND_UP);
    if (!amountInMax.isFinite() || amountInMax.isLessThanOrEqualTo(0)) continue;
    // a hop asking for more than its reserves gives negative amounts, the forward trade catches it
    const forward = tryTrade(() => router.tradeExactIn(amountInMax, NO_SLIPPAGE));
    if (!forward || forward.amount_out_min.isLessThan(_amountOut)) continue;
    if (!best || amountInMax.isLessThan(best.amount_in_max)) {
      best = { amount_in_max: amountInMax, amount_out: _amountOut, path: trade.path, pairs: route };
    }
  }
  return best;
};

/**
 * Price impact in percent (2 decimals) of a trade along a route, fees excluded:
 * how much less the trade returns than the route's mid price would give
 */
export const computePriceImpact = (route, path, amountIn, amountOut) => {
  if (!route || !path || route.length === 0 || path.length !== route.length + 1) return "0";
  // getQuote follows the SDK reserve order (reserve_0 belongs to token_1, see Pair.getOutputAmount),
  // reserveOf does not, so the mid price is taken from getQuote
  let midPrice = new BigNumber(1);
  for (let i = 0; i < route.length; i++) {
    midPrice = midPrice.times(route[i].getQuote(path[i], new BigNumber(1)));
  }
  const expectedOut = new BigNumber(amountIn).times(midPrice).times(FEE_FACTOR.pow(route.length));
  if (!expectedOut.isFinite() || expectedOut.isZero()) return "0";
  const impact = new BigNumber(1).minus(new BigNumber(amountOut).div(expectedOut)).times(100);
  return BigNumber.max(impact, 0).toFixed(2);
};

/**
 * Quotes a fixed route (its pairs in path order) without slippage, or null when it can not be traded
 * exactIn: { amount_in, amount_out_min, path } · exactOut: { amount_out, amount_in_max, path }
 */
export const quoteRoute = (route, path, amount, { exactIn = true } = {}) => {
  if (!route || !path || route.length === 0 || path.length !== route.length + 1) return null;
  return tryTrade(() => {
    const router = new Router(route, path[0], path[path.length - 1]);
    return exactIn
      ? router.tradeExactIn(new BigNumber(amount), NO_SLIPPAGE)
      : router.tradeExactOut(new BigNumber(amount), NO_SLIPPAGE);
  });
};
